import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { RondleidingService } from './rondleiding.service';
import { IRondleidingStap } from './rondleidingen';
import { ballonPlek, IBallonPlek, IRechthoek } from '../../../helpers/onboarding';

/** Ruimte tussen het doel en de rand van de spotlight. */
const RAND = 6;
/** Hoe lang we na een routewissel op het doel wachten; zonder route korter. */
const WACHT_MET_ROUTE = 5000;
const WACHT_ZONDER_ROUTE = 1200;
const ZOEK_STAP = 100;
/** Zolang de stap open is: doel opnieuw meten (en zoeken als het er nog niet was). */
const BIJHOUDEN = 400;

/**
 * Rondleiding (variant A, docs/ONBOARDING.md): het scherm donker met een
 * spotlight op het doel, een lichte ballon met tekst, stippen, "Skip tour" en
 * "Next"/"Done". Eigen onderdeel, geen bibliotheek.
 *
 * De spotlight is een vast vak om het doel met een enorme schaduw
 * (`box-shadow: 0 0 0 9999px`); hij vangt geen klikken, dus het doel blijft
 * te bedienen. Een klik óp het doel gaat door naar de volgende stap (behalve
 * bij de laatste: die rondt je af met "Done").
 *
 * Staat het doel niet (zichtbaar) op het scherm, dan staat de ballon midden
 * onderin zonder spotlight. Verschijnt het doel later alsnog (bijv. de keuze
 * Wedstrijd/Training na het kiezen van een bestand), dan springt de
 * spotlight er alsnog naartoe.
 */
@Component({
  selector: 'app-rondleiding',
  standalone: true,
  imports: [TranslateModule],
  templateUrl: './rondleiding.component.html',
  styleUrls: ['./rondleiding.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RondleidingComponent {
  public readonly rondleiding = inject(RondleidingService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private readonly ballonRef = viewChild<ElementRef<HTMLElement>>('ballon');
  private readonly volgendeRef = viewChild<ElementRef<HTMLButtonElement>>('volgende');

  public readonly spot = signal<IRechthoek | null>(null);
  public readonly plek = signal<IBallonPlek | null>(null);
  public readonly zichtbaar = signal(false);
  public readonly stippen = computed(() => Array.from({ length: this.rondleiding.aantal() }, (_, i) => i));

  private doelEl: HTMLElement | null = null;
  private stap: IRondleidingStap | null = null;
  private zoekTimer: ReturnType<typeof setTimeout> | null = null;
  private bijhoudTimer: ReturnType<typeof setInterval> | null = null;
  private frame = 0;

  constructor() {
    effect(() => {
      const stap = this.rondleiding.stap();
      // De index erbij: twee stappen met hetzelfde doel moeten ook opnieuw.
      this.rondleiding.index();
      untracked(() => this.toonStap(stap));
    });

    const opnieuwMeten = () => this.planMeting();
    const toets = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && this.rondleiding.actief()) {
        this.rondleiding.overslaan();
      }
    };
    const klik = (e: MouseEvent) => this.klikOpDoel(e);
    window.addEventListener('resize', opnieuwMeten);
    // Scrollen in welk vak dan ook (de pagina schuift niet altijd zelf).
    window.addEventListener('scroll', opnieuwMeten, true);
    document.addEventListener('keydown', toets);
    document.addEventListener('click', klik, true);
    this.destroyRef.onDestroy(() => {
      window.removeEventListener('resize', opnieuwMeten);
      window.removeEventListener('scroll', opnieuwMeten, true);
      document.removeEventListener('keydown', toets);
      document.removeEventListener('click', klik, true);
      this.stopTimers();
    });
  }

  public onVolgende(): void {
    this.rondleiding.volgende();
  }

  public onOverslaan(): void {
    this.rondleiding.overslaan();
  }

  private toonStap(stap: IRondleidingStap | null): void {
    this.stopTimers();
    this.stap = stap;
    this.doelEl = null;
    this.spot.set(null);
    this.plek.set(null);
    this.zichtbaar.set(false);
    if (!stap) {
      return;
    }

    const pad = this.router.url.split('?')[0].split('#')[0];
    if (stap.route && pad !== stap.route) {
      void this.router.navigateByUrl(stap.route);
    }

    const tot = Date.now() + (stap.route ? WACHT_MET_ROUTE : WACHT_ZONDER_ROUTE);
    const zoek = () => {
      if (this.stap !== stap) {
        return;
      }
      const el = this.vindDoel(stap.doel);
      if (el || Date.now() >= tot) {
        this.zoekTimer = null;
        this.doelEl = el;
        this.toon();
        return;
      }
      this.zoekTimer = setTimeout(zoek, ZOEK_STAP);
    };
    zoek();
  }

  /** Doel gevonden (of opgegeven): meten, tonen en bijhouden. */
  private toon(): void {
    if (this.doelEl) {
      this.inBeeld(this.doelEl);
    }
    // Eén frame wachten: de ballon heeft dan zijn tekst en dus zijn maat.
    requestAnimationFrame(() => {
      this.meet();
      this.zichtbaar.set(true);
      this.volgendeRef()?.nativeElement.focus({ preventScroll: true });
    });
    this.bijhoudTimer = setInterval(() => {
      if (!this.stap) {
        return;
      }
      if (!this.doelEl || !this.doelEl.isConnected || !this.isZichtbaar(this.doelEl)) {
        const el = this.vindDoel(this.stap.doel);
        if (el !== this.doelEl) {
          this.doelEl = el;
          if (el) {
            this.inBeeld(el);
          }
        }
      }
      this.meet();
    }, BIJHOUDEN);
  }

  private planMeting(): void {
    if (!this.zichtbaar() || this.frame) {
      return;
    }
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.meet();
    });
  }

  private meet(): void {
    const ballon = this.ballonRef()?.nativeElement;
    if (!ballon || !this.stap) {
      return;
    }
    let spot: IRechthoek | null = null;
    if (this.doelEl && this.isZichtbaar(this.doelEl)) {
      const r = this.doelEl.getBoundingClientRect();
      // Buiten beeld en niet in beeld te schuiven: dan maar zonder spotlight.
      if (this.inVenster(r)) {
        // Binnen het venster houden, met ruimte voor de groene rand: een
        // doel tegen de rand (de tabbalk onderin) hield anders een halve rand.
        const top = Math.max(RAND, r.top - RAND);
        const left = Math.max(RAND, r.left - RAND);
        const onder = Math.min(window.innerHeight - RAND, r.bottom + RAND);
        const rechts = Math.min(window.innerWidth - RAND, r.right + RAND);
        spot = { top, left, width: Math.max(0, rechts - left), height: Math.max(0, onder - top) };
      }
    }
    this.spot.set(spot);
    this.plek.set(ballonPlek(spot, { width: ballon.offsetWidth, height: ballon.offsetHeight },
      { width: window.innerWidth, height: window.innerHeight }));
  }

  /** Het eerste element bij de selector dat echt op het scherm staat. */
  private vindDoel(selector: string): HTMLElement | null {
    const kandidaten = Array.from(document.querySelectorAll<HTMLElement>(selector));
    return kandidaten.find(el => this.isZichtbaar(el)) ?? null;
  }

  private isZichtbaar(el: HTMLElement): boolean {
    const r = el.getBoundingClientRect();
    if (r.width < 1 || r.height < 1) {
      return false;
    }
    const stijl = getComputedStyle(el);
    return stijl.visibility !== 'hidden' && stijl.display !== 'none' && Number(stijl.opacity) !== 0;
  }

  /** Valt de rechthoek (deels) in het venster? */
  private inVenster(r: DOMRect): boolean {
    return r.bottom > 0 && r.right > 0 && r.top < window.innerHeight && r.left < window.innerWidth;
  }

  /**
   * Het doel in beeld schuiven als het er (deels) buiten valt; ook binnen
   * een zijwaarts schuivende balk, zoals de tabbalk op de telefoon.
   */
  private inBeeld(el: HTMLElement): void {
    const r = el.getBoundingClientRect();
    if (r.top < 0 || r.left < 0 || r.bottom > window.innerHeight || r.right > window.innerWidth) {
      el.scrollIntoView({ block: 'center', inline: 'center', behavior: 'auto' });
    }
  }

  private klikOpDoel(e: MouseEvent): void {
    if (!this.zichtbaar() || !this.doelEl || this.rondleiding.laatste()) {
      return;
    }
    const doel = e.target as HTMLElement | null;
    // In een invoerveld klikken is gaan typen (bijv. een link plakken), nog
    // geen stap verder.
    if (doel?.closest?.('input, textarea, select')) {
      return;
    }
    if (doel && this.doelEl.contains(doel)) {
      // Eerst laat de klik zijn eigen werk doen (navigeren, bestand kiezen).
      setTimeout(() => this.rondleiding.volgende(), 0);
    }
  }

  private stopTimers(): void {
    if (this.zoekTimer) {
      clearTimeout(this.zoekTimer);
      this.zoekTimer = null;
    }
    if (this.bijhoudTimer) {
      clearInterval(this.bijhoudTimer);
      this.bijhoudTimer = null;
    }
    if (this.frame) {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
    }
  }
}
